/**
 * coachRun.ts — the one place "which steps, in what mode" logic lives.
 *
 * Pure: no DOM, no React, no storage. `decideAutoRun` takes plain inputs
 * (a tour, the caller's best-effort persisted record, a couple of booleans)
 * and returns a decision or `null`. CoachMarkProvider is the only caller —
 * it supplies `record` via lib/coachJourney.ts's `getTourRecord` and
 * `newUserEligible`/`autoSuppressed` via `isAutoShowEligible`/
 * `autoToursSuppressed`.
 */

import type { CoachStep, CoachTour } from './coachTypes';
import type { TourRecordDTO } from '../utils/coachMarks';

export type CoachRunMode = 'full' | 'update';

/** All steps for `mode: 'full'`; only steps added after `fromVersion` for
 *  `mode: 'update'` — the "what's new" mini-run. */
export function stepsForRun(tour: CoachTour, mode: CoachRunMode, fromVersion: number): CoachStep[] {
  if (mode === 'full') return tour.steps;
  return tour.steps.filter((step) => step.since > fromVersion);
}

/**
 * Never mutate the user's UI state to make the tour work (no auto-expanding
 * a collapsed sidebar, no auto-switching a composer tab) — so the fallback
 * for "nothing resolved" is the required-only subset, centred by the caller.
 * Optional steps exist precisely so they can vanish silently instead of
 * forcing that fallback; they never enter it themselves, and if every step
 * is optional and none resolve, there is nothing left to show.
 */
export function resolveRunSteps(steps: CoachStep[], resolves: (anchor: string) => boolean): CoachStep[] {
  const anyResolved = steps.some((step) => resolves(step.anchor));
  // Nothing on the page: centred fallback of the required steps ([] when
  // every step is optional — nothing worth starting).
  if (!anyResolved) return steps.filter((step) => !step.optional);
  // Required steps must be on the page now. Optional steps stay in the run
  // whether or not they resolve yet: they are skipped live if their target is
  // absent when reached, and shown if it has appeared by then — e.g. the
  // "Use this template" button inside the modal the previous card told the
  // person to open. They are not counted in "N of M" (see countedSlots).
  return steps.filter((step) => (step.optional ? true : resolves(step.anchor)));
}

export interface DecideAutoRunInput {
  tour: CoachTour;
  /** Best-effort persisted record for this tour, server → cache → legacy. */
  record: TourRecordDTO | null;
  /** isAutoShowEligible(user, viewportWidth, pathname) — journey user, not
   *  gated/hidden, on a desktop surface. */
  newUserEligible: boolean;
  /** autoToursSuppressed(user) — the user has closed two auto tours without
   *  finishing either. */
  autoSuppressed: boolean;
  isMobile: boolean;
  /** A tour or nudge is already running. */
  runActive: boolean;
  /** A manual start is parked waiting on a route hop. */
  pendingActive: boolean;
}

export interface DecideAutoRunResult {
  mode: CoachRunMode;
  fromVersion: number;
  steps: CoachStep[];
}

/**
 * Decides whether an auto-run should begin right now, and if so what kind.
 *
 * - mobile / a run already active / a manual start pending a route hop → never.
 * - a record exists and is behind the current version → `update`, but only
 *   if that gap actually added steps (a version bump for copy-only fixes
 *   never re-opens the tour — see build-tours.js's E_VERSION_NO_NEW_STEPS).
 *   Applies regardless of whether the prior run was completed or dismissed.
 * - a record exists at (or past) the current version → never; nothing new
 *   to show, and dismissed-at-current-version must not re-trigger `full`.
 * - no record at all → `full`, but only for a `new-user` tour, only for an
 *   eligible journey user, and only while auto tours aren't suppressed.
 */
export function decideAutoRun(input: DecideAutoRunInput): DecideAutoRunResult | null {
  const { tour, record, newUserEligible, autoSuppressed, isMobile, runActive, pendingActive } = input;

  if (isMobile || runActive || pendingActive) return null;

  if (record) {
    if (record.version >= tour.version) return null;
    const steps = stepsForRun(tour, 'update', record.version);
    if (steps.length === 0) return null;
    return { mode: 'update', fromVersion: record.version, steps };
  }

  if (tour.auto === 'new-user' && newUserEligible && !autoSuppressed) {
    return { mode: 'full', fromVersion: 0, steps: stepsForRun(tour, 'full', 0) };
  }

  return null;
}

/** Page state as published by `data-coach-state` attributes (lib/coachDom). */
export type CoachStateLike = Record<string, string>;

/**
 * Group a run's steps into SLOTS — what "N of M" counts. Consecutive steps
 * that all carry `if` on the same key are variants of one card and share a
 * slot; everything else is a slot of its own. The compiler guarantees a
 * same-anchor variant group is consecutive, same-key, distinct-value.
 */
export function slotsOf(steps: CoachStep[]): CoachStep[][] {
  const slots: CoachStep[][] = [];
  for (const step of steps) {
    const last = slots[slots.length - 1];
    // Variants share a slot when they are consecutive and conditioned on the
    // same key. They may point at DIFFERENT anchors — "pick a template" points
    // at the grid when nothing is searched and at the first result after a
    // search — which is exactly why the anchor is not part of the grouping.
    if (last && step.if && last[0].if && last[0].if.key === step.if.key) {
      last.push(step);
    } else {
      slots.push([step]);
    }
  }
  return slots;
}

/** The variant to show right now: the first whose `if` matches the page
 *  state, else the first unconditioned one, else null (nothing fits). */
export function pickVariant(slot: CoachStep[], state: CoachStateLike): CoachStep | null {
  for (const step of slot) {
    if (step.if && state[step.if.key] === step.if.value) return step;
  }
  return slot.find((step) => !step.if) ?? null;
}

export function currentStep(steps: CoachStep[], slotIndex: number, state: CoachStateLike): CoachStep | null {
  const slot = slotsOf(steps)[slotIndex];
  return slot ? pickVariant(slot, state) : null;
}

/** A slot is optional when every variant in it is. */
export function isOptionalSlot(slot: CoachStep[]): boolean {
  return slot.every((step) => !!step.optional);
}

/** The slots "N of M" counts — optional (appear-if-present) cards are left out. */
export function countedSlots(slots: CoachStep[][]): CoachStep[][] {
  return slots.filter((slot) => !isOptionalSlot(slot));
}

/** 1-based position of the counted slot at or just before `index`. */
export function countedNumber(slots: CoachStep[][], index: number): number {
  let n = 0;
  for (let i = 0; i <= index && i < slots.length; i++) {
    if (!isOptionalSlot(slots[i])) n += 1;
  }
  return Math.max(1, n);
}

export type TourGlowState = 'new' | 'update';

/** Why the bulb should glow for this tour, or null: 'new' = never seen;
 *  'update' = seen an older version that has new `since` cards. */
export function tourGlowState(tour: CoachTour, record: TourRecordDTO | null): TourGlowState | null {
  if (!tour || tour.steps.length === 0) return null;
  if (!record) return 'new';
  if (record.version < tour.version && stepsForRun(tour, 'update', record.version).length > 0) return 'update';
  return null;
}
